/**
 * Analytics utility for PostHog tracking
 * Handles user events, page views, and referral tracking
 */

import posthog from 'posthog-js';

// Initialize PostHog - Replace with your actual API key from PostHog Project Settings → Installation
posthog.init('phc_eleT0gvfNYMWQSbCgXgjFuaDJcSOL9q6rExVITf1lFL', {
  api_host: 'https://app.posthog.com',
  // Enable debug in development
  debug: process.env.NODE_ENV === 'development',
  // Track page views automatically
  capture_pageview: false, // We'll handle manually for more control
});

const analytics = {
  /**
   * Track custom events
   * @param {string} event - Event name
   * @param {object} properties - Event properties
   */
  track: (event, properties = {}) => {
    try {
      posthog.capture(event, {
        timestamp: new Date().toISOString(),
        ...properties
      });
    } catch (error) {
      console.error('Analytics tracking error:', error);
    }
  },

  /**
   * Track page views
   * @param {string} page - Page path/name
   * @param {object} properties - Additional page properties
   */
  page: (page, properties = {}) => {
    try {
      posthog.capture('$pageview', {
        $current_url: window.location.href,
        pathname: window.location.pathname,
        page,
        timestamp: new Date().toISOString(),
        ...properties
      });
    } catch (error) {
      console.error('Page view tracking error:', error);
    }
  },

  /**
   * Identify users for cross-session tracking
   * @param {string} userId - User ID
   * @param {object} traits - User properties
   */
  identify: (userId, traits = {}) => {
    try {
      posthog.identify(userId, {
        ...traits,
        identified_at: new Date().toISOString()
      });
    } catch (error) {
      console.error('User identification error:', error);
    }
  },

  /**
   * Track user login events
   * @param {object} user - User object
   * @param {string} method - Login method (email, google, etc.)
   */
  trackLogin: (user, method = 'email') => {
    analytics.track('user_logged_in', {
      user_id: user.id,
      username: user.username,
      login_method: method,
      email: user.email
    });
    
    // Identify user for future tracking
    analytics.identify(user.id, {
      email: user.email,
      username: user.username,
      first_name: user.firstName,
      last_name: user.lastName,
      is_admin: user.isAdmin,
      created_at: user.createdAt
    });
  },

  /**
   * Track user registration
   * @param {object} user - User object
   * @param {string} referralCode - Referral code if any
   * @param {string} source - Source of registration (e.g., PWA)
   */
  trackRegistration: (user, referralCode = null, source = 'PWA') => {
    analytics.track('user_registered', {
      user_id: user.id,
      username: user.username,
      email: user.email,
      referral_code: referralCode,
      source: source
    });
    
    // Identify new user
    analytics.identify(user.id, {
      email: user.email,
      username: user.username,
      first_name: user.firstName,
      last_name: user.lastName,
      is_admin: user.isAdmin,
      created_at: user.createdAt,
      source: source
    });
  },

  /**
   * Track referral visits
   * @param {string} referralCode - Referral code
   * @param {string} landingPage - Landing page URL
   */
  trackReferralVisit: (referralCode, landingPage) => {
    analytics.track('referral_visit', {
      referral_code: referralCode,
      landing_page: landingPage,
      user_agent: navigator.userAgent
    });
  },

  /**
   * Track QR code scans
   * @param {string} referralCode - Referral code from QR
   */
  trackQRCodeScan: (referralCode) => {
    analytics.track('qr_code_scanned', {
      referral_code: referralCode,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString()
    });
  },

  /**
   * Track Daily Flush visits
   */
  trackDailyFlushVisit: () => {
    analytics.track('daily_flush_visited', {
      page: 'daily-flush',
      timestamp: new Date().toISOString()
    });
  },

  /**
   * Track map load (home page with map)
   */
  trackMapLoad: () => {
    analytics.track('map_loaded', {
      page: 'home',
      timestamp: new Date().toISOString()
    });
  },

  /**
   * Track social media referral from UTM parameters
   * @param {string} source - UTM source (ig, fb, twitter, etc.)
   */
  trackSocialReferral: (source) => {
    analytics.track('social_referral', {
      source: source,
      medium: 'social',
      timestamp: new Date().toISOString()
    });
  },

  /**
   * Track restroom submission
   * @param {object} restroomData - Restroom data submitted
   */
  trackRestroomSubmission: (restroomData) => {
    analytics.track('restroom_submitted', {
      category: restroomData.category,
      rating: restroomData.rating,
      hasTruckParking: restroomData.hasTruckParking || false,
      isAdmin: restroomData.isAdmin || false,
      timestamp: new Date().toISOString()
    });
  },

  /**
   * Track review submission
   * @param {object} reviewData - Review data submitted
   */
  trackReviewSubmission: (reviewData) => {
    analytics.track('review_submitted', {
      rating: reviewData.rating,
      restroom_id: reviewData.restroomId,
      restroom_name: reviewData.restroomName,
      timestamp: new Date().toISOString()
    });
  },

  /**
   * Track restroom interactions
   * @param {string} action - Action type (view, review, report)
   * @param {object} data - Restroom data
   */
  trackRestroomInteraction: (action, data) => {
    analytics.track(`restroom_${action}`, {
      restroom_id: data.id,
      restroom_name: data.name,
      category: data.category,
      rating: data.rating,
      ...data
    });
  },

  /**
   * Track app store badge clicks
   * @param {string} store - Store type (ios or android)
   * @param {string} source - Source of click (banner, download_page, etc.)
   */
  trackAppStoreBadgeClick: (store, source = 'banner') => {
    analytics.track('app_store_badge_clicked', {
      store: store,
      source: source,
      timestamp: new Date().toISOString()
    });
  },

  /**
   * Track "Send to Phone" button clicks
   */
  trackSendToPhoneClick: () => {
    analytics.track('send_to_phone_clicked', {
      source: 'desktop_banner',
      timestamp: new Date().toISOString()
    });
  },

  /**
   * Track verified restroom event
   * @param {string} restroomId - ID of the restroom
   * @param {string} restroomName - Name of the restroom
   * @param {string} source - Source of verification (Admin, GooglePlaces, 2nd Review)
   */
  trackVerifiedRestroom: (restroomId, restroomName, source) => {
    analytics.track('Verified Restroom', {
      restroom_id: restroomId,
      restroom_name: restroomName,
      source: source,
      timestamp: new Date().toISOString()
    });
  },

  /**
   * Reset user identification (for logout)
   */
  reset: () => {
    try {
      posthog.reset();
    } catch (error) {
      console.error('Analytics reset error:', error);
    }
  }
};

export default analytics;
